import { c as _c } from "react/compiler-runtime";
import { Form, Formik } from 'formik';
import { useContext } from 'react';
import { APIDestination, DestinationRateLimitPeriod } from '../../../../../../../../typings/Destination.interface';
import { cleanseFormErrorObject } from '../../../../../utils/form';
import Button from '../../../../common/base/Button';
import { Div } from '../../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { useToasts } from '../../../../common/Toast';
import { DestinationFeatureType, MaxThroughputRateFeature } from '../../Connections/Forms/form_features';
import { getDestinationDeliveryPolicy } from '../../Connections/Forms/destination_configuration';
interface MaxDeliveryRateFormValues {
  enable_rate_limit: boolean;
  config: {
    rate_limit?: string;
    rate_limit_period?: DestinationRateLimitPeriod;
  };
}
const max_delivery_rate_form_props = {
  postprocessValues: (values: MaxDeliveryRateFormValues) => {
    return {
      config: {
        delivery_policy: {
          rate: values.enable_rate_limit ? Number(values.config.rate_limit) : null,
          period: values.enable_rate_limit ? values.config.rate_limit_period : null,
          groups: null
        }
      }
    };
  },
  getInitialValues: (destination?: APIDestination): MaxDeliveryRateFormValues => {
    if (destination) {
      const delivery_policy = getDestinationDeliveryPolicy(destination);
      return {
        enable_rate_limit: !!delivery_policy?.rate,
        config: {
          rate_limit: delivery_policy?.rate ? String(delivery_policy.rate) : undefined,
          rate_limit_period: delivery_policy?.period || 'second'
        }
      };
    }
    return {
      enable_rate_limit: false,
      config: {
        rate_limit: undefined,
        rate_limit_period: 'second'
      }
    };
  },
  validate: async (values: MaxDeliveryRateFormValues) => {
    const errors: Record<string, string> = {};
    if (values.enable_rate_limit && Number(values.config?.rate_limit) <= 0) {
      errors['config.rate_limit'] = 'Must be greater than 0';
    }
    return cleanseFormErrorObject(errors);
  }
};
const SetMaxDeliveryRate: React.FC<{
  context: {
    destination: APIDestination | null;
  };
  mutateContext: (context: Partial<{
    destination: APIDestination;
  }>) => void;
  nextStep: () => void;
}> = t0 => {
  const $ = _c(16);
  const {
    context: t1,
    mutateContext,
    nextStep
  } = t0;
  const {
    destination
  } = t1;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      type: "MAX_DELIVERY_RATE",
      managed: false
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const feature = t2;
  const t3 = destination?.id;
  let t4;
  if ($[1] !== destination) {
    t4 = max_delivery_rate_form_props.getInitialValues(destination || undefined);
    $[1] = destination;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== HookdeckAPI || $[4] !== addToast || $[5] !== destination || $[6] !== mutateContext || $[7] !== nextStep) {
    t5 = (v, t6) => {
      const {
        resetForm
      } = t6;
      if (!destination) {
        addToast("error", "No destination found");
        return;
      }
      const values = max_delivery_rate_form_props.postprocessValues(v);
      return HookdeckAPI.destinations.update(destination.id, values).then(updated_destination => {
        addToast("success", `Destination ${updated_destination.name} max delivery rate updated`);
        resetForm({
          values: max_delivery_rate_form_props.getInitialValues(updated_destination)
        });
        mutateContext({
          destination: updated_destination
        });
        nextStep();
      }).catch(e => {
        addToast("error", `An error occurred while updating max delivery rate${e.response?.data[0] ? `: ${e.response.data[0]}` : ""}`);
      });
    };
    $[3] = HookdeckAPI;
    $[4] = addToast;
    $[5] = destination;
    $[6] = mutateContext;
    $[7] = nextStep;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== nextStep) {
    t6 = t7 => {
      const {
        isValid,
        isSubmitting,
        handleSubmit,
        dirty
      } = t7;
      return <Form data-testid="guided-max-delivery-form" onSubmit={handleSubmit}><Div><MaxThroughputRateFeature feature={feature} prefix="" /></Div><Div flex={{
          gap: 3
        }} m={{
          t: 8
        }}><Button submit={dirty} onClick={!dirty ? nextStep : undefined} disabled={dirty ? !isValid || isSubmitting : false} outline={true} icon={isSubmitting ? "loading" : undefined}>{dirty ? "Update" : "Next"}</Button></Div></Form>;
    };
    $[9] = nextStep;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5 || $[14] !== t6) {
    t7 = <Formik key={t3} initialValues={t4} validateOnMount={true} validate={max_delivery_rate_form_props.validate} onSubmit={t5}>{t6}</Formik>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
};
export default SetMaxDeliveryRate;